import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import { useAuth } from "@/components/AuthProvider";
import { HubPresenceAvatarStack } from "@/components/HubPresenceAvatarStack";
import {
  fetchHubPresenceCurrent,
  hubPresenceCurrentQueryKey,
} from "@/lib/hubPresenceApi";
import { resolveHubPresenceRoute } from "@/lib/hubPresenceRoute";

const MAX_VISIBLE_AVATARS = 3;

/** Compact current-page presence stack for the hub header. Omits the signed-in user. */
export function CurrentPagePresence(): JSX.Element | null {
  const { session } = useAuth();
  const location = useLocation();
  const route = useMemo(
    () => resolveHubPresenceRoute(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const viewerUserId = session?.user?.id ?? null;
  const enabled = viewerUserId != null && viewerUserId !== "";

  const presenceQuery = useQuery({
    queryKey: hubPresenceCurrentQueryKey(viewerUserId ?? "unauthenticated", route.routeKey),
    queryFn: ({ signal }) => fetchHubPresenceCurrent(route.routeKey, signal),
    enabled: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const people = useMemo(() => {
    const viewers = presenceQuery.data?.viewers ?? [];
    return viewers
      .filter((viewer) => viewer.userId !== viewerUserId)
      .map((viewer) => ({
        userId: viewer.userId,
        email: viewer.email,
        displayName: viewer.displayName,
        avatarUrl: viewer.avatarUrl,
        presenceState: viewer.presenceState,
        tabCount: viewer.tabCount,
      }));
  }, [presenceQuery.data?.viewers, viewerUserId]);

  if (!enabled || people.length === 0) {
    return null;
  }

  return (
    <HubPresenceAvatarStack
      people={people}
      maxVisible={MAX_VISIBLE_AVATARS}
      labelPrefix="Viewing this page"
      compact
    />
  );
}
